/**
 * Order + history tabs state, one form per conversation. Talks to `/api/v1/pos` (or the mock) only.
 * - every cart change → live quote after `quoteDelayMs` (debounced; stale answers are dropped)
 * - coupon: checked once at entry (discount frozen, like the POS sale page); a pack change makes it stale
 * - save: one client_id per click; a click that got no answer keeps its id so pressing save again is safe
 * - a save the POS has not answered (`openOrders`, from the context or the save's error) is followed up by the server
 *   (api/app/pos_followup.py); the tab shows it instead of a new form until the realtime "pos_order" event (or
 *   "ตรวจผลตอนนี้") brings its end: done → the saved screen · refused → back to the form with the reason
 * - "ยังไม่โอน · ส่ง QR ให้ลูกค้า" (api/app/pos_pending.py): the save is a POS held bill (`status: 'held'`, context
 *   `held_orders`); `stageQr` puts its PromptPay QR in the composer, `confirmPay` ("ยืนยันชำระ") makes the POS order
 * - a chat with a held order shows it first (owner 18 ต.ค. 69 "แค่รีเฟสระบบก็ขึ้นมาให้ลงออเดอร์ใหม่ได้ อยากให้ดูบริบทด้วยว่าลูกค้า
 *   รอโอน"): `heldGate` — no new-order form and nothing filled into it until "ลงออเดอร์ใหม่อีกใบ" (`openNewOrder`); after a
 *   reload it comes from the server context again. "แก้ไขออเดอร์" (`startEdit` / `saveEdit`) edits the SAME held bill
 * - "บัตรเครดิต · ส่งลิงก์ให้ลูกค้า" (api/app/pos_card.py): the save is a POS card payment link (`status: 'held'` with `card`,
 *   in `held_orders` too); the customer pays it and the POS makes the order. `cancelOrder` ("ยกเลิกลิงก์" / "ยกเลิกพักบิล"),
 *   `renewCard` ("ต่ออายุ"), `refreshCard` ("ตรวจสถานะตอนนี้"); "แก้ไขออเดอร์" of a link = the old link cancelled + a new one
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getPosApi, PosApiError } from '../api/pos'
import type { PrefillAddress } from '../api/script/types'
import type { RealtimeEvent } from '../api/types'
import type {
  ChatOrderRef,
  PosAmountDiff,
  PosCartItem,
  PosCatalog,
  PosChatOrderState,
  PosContext,
  PosCustomer,
  PosCustomerCreate,
  PosCustomerCreated,
  PosHistory,
  PosHistoryOrder,
  PosHoldEditBody,
  PosOpenOrder,
  PosOpenPack,
  PosOrderResult,
  PosPayBody,
  PosPinResult,
  PosQr,
  PosQuote,
  PosRenewBody,
  PosSharedPlace,
  PosShipping,
} from '../api/pos/types'
import {
  cartBody,
  deliveryOf,
  emptyForm,
  fingerprint,
  firstOpenDay,
  heldForm,
  newClientId,
  orderBody,
  packForName,
  saveIssues,
  withQty,
  type OrderForm,
} from '../lib/order'
import { dayKey } from '../lib/inbox'
import { useClock } from './clock'
import { useInbox } from './inbox'
import { useLibrary } from './library'

export interface QuoteState {
  state: 'idle' | 'loading' | 'ok' | 'error'
  data: PosQuote | null
  error: string | null
  /** Cart the data/error belongs to (fingerprint). */
  key: string | null
}

export interface Loadable<T> {
  state: 'idle' | 'loading' | 'ok' | 'error'
  data: T | null
  error: string | null
}

const IDLE_QUOTE: QuoteState = { state: 'idle', data: null, error: null, key: null }

/** What opens the order tab's "เพิ่มลูกค้าใหม่ใน POS" form, filled (components/side/order/CustomerBlock.vue). */
export interface CreateRequest {
  mobile: string
  at: number
  /** The place from the chat (the summary handoff) — only empty fields are filled. */
  address?: PrefillAddress | null
  shipping?: PosShipping | null
  /** The recipient's name the chat gave ("เติมจากแชททั้งหมด") — over an empty name or the chat's display name only. */
  name?: string | null
  /** Fill empty fields only: the phone too, and the carrier only while no address was typed ("เติมจากแชททั้งหมด"). */
  keep?: boolean
}

const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e))

/** What "ยืนยันชำระ" ended with: done (the POS order) · mismatch (ask the second click) · error (a calm Thai reason). */
export type PayOutcome =
  | { kind: 'done'; order: ChatOrderRef }
  | { kind: 'mismatch'; diff: PosAmountDiff; message: string }
  | { kind: 'error'; message: string; settled: boolean }

export const useOrders = defineStore('orders', () => {
  const clock = useClock()
  const api = () => getPosApi()

  const catalog = ref<PosCatalog | null>(null)
  const catalogError = ref<string | null>(null)
  const contexts = ref<Record<string, PosContext>>({})
  const contextErrors = ref<Record<string, string | null>>({})
  const forms = ref<Record<string, OrderForm>>({})
  const quotes = ref<Record<string, QuoteState>>({})
  const customers = ref<Record<string, PosCustomer>>({})
  const histories = ref<Record<string, Loadable<PosHistory>>>({})
  const saving = ref<Record<string, boolean>>({})
  const saveErrors = ref<Record<string, string | null>>({})
  const results = ref<Record<string, PosOrderResult | null>>({})
  /** The click that has not been answered yet: same cart → same client_id. */
  const pendingClicks = ref<Record<string, { clientId: string; fp: string } | null>>({})
  /** A save of the chat the POS has not answered yet (any staff's, or the AI's): no new order until it ends. */
  const openOrders = ref<Record<string, PosOpenOrder | null>>({})
  /** "ตรวจผลตอนนี้" running. */
  const checking = ref<Record<string, boolean>>({})
  /** Debounce for live quotes. 0 = quote immediately (tests). */
  const quoteDelayMs = ref(400)

  /** The POS history call does not say whether there is a pin: keep what the search said. */
  function remember(c: PosCustomer): void {
    const old = customers.value[c.id]
    customers.value[c.id] = { ...old, ...c, has_pin: c.has_pin ?? old?.has_pin ?? null }
  }

  // ------------------------------------------------ loading
  let catalogLoad: Promise<void> | null = null
  function loadCatalog(force = false): Promise<void> {
    if (catalog.value && !force) return Promise.resolve()
    if (catalogLoad && !force) return catalogLoad
    catalogLoad = (async () => {
      try {
        catalog.value = await api().catalog()
        catalogError.value = null
      } catch (e) {
        catalogError.value = errText(e)
      } finally {
        catalogLoad = null
      }
    })()
    return catalogLoad
  }

  function form(convId: string): OrderForm {
    let f = forms.value[convId]
    if (!f) {
      f = emptyForm(clock.now, contexts.value[convId]?.customer_id ?? null, catalog.value)
      forms.value[convId] = f
    }
    return f
  }

  async function loadHistory(customerId: string): Promise<void> {
    const cur = histories.value[customerId]
    histories.value[customerId] = { state: 'loading', data: cur?.data ?? null, error: null }
    try {
      const h = await api().customerOrders(customerId, 20)
      histories.value[customerId] = { state: 'ok', data: h, error: null }
      if (h.customer) remember(h.customer)
    } catch (e) {
      histories.value[customerId] = { state: 'error', data: cur?.data ?? null, error: errText(e) }
    }
  }

  // ------------------------------------------------ who the chat is linked to (here or on another screen)
  /** Bumped by a link / unlink / create-and-link on this screen: a context answer asked before it does not undo it. */
  const linkEpoch = new Map<string, number>()
  const linkWatchers = new Set<(convId: string) => void>()
  /** The chat's POS customer changed (this screen's own link / unlink, or the context says a colleague's): stores/phonelink.ts. */
  function onLinkChange(fn: (convId: string) => void): () => void {
    linkWatchers.add(fn)
    return () => linkWatchers.delete(fn)
  }
  function tellLink(convId: string): void {
    for (const fn of linkWatchers) {
      try {
        fn(convId)
      } catch {
        // a watcher's failure must not break the order tab
      }
    }
  }
  function linkedHere(convId: string): void {
    linkEpoch.set(convId, (linkEpoch.get(convId) ?? 0) + 1)
    tellLink(convId)
  }

  /** A form nobody started for the linked customer (no pack, menus, open pack, coupon or source): it may follow the link. */
  function untouched(f: OrderForm, prevCid: string | null): boolean {
    return (
      (f.customerId ?? null) === prevCid &&
      !f.setMenuId &&
      !f.continueHeaderId &&
      !f.items.length &&
      f.coupon.state === 'none' &&
      !f.source
    )
  }

  /** The context says the chat was linked to another POS customer / unlinked on another screen (owner 18 ต.ค. 69 "ไม่ต้อง
   *  รีเฟสหน้าเอง"): an untouched new-order form follows it, the new customer's history loads, the phone buttons re-check. */
  function linkedElsewhere(convId: string, prevCid: string | null, nextCid: string | null): void {
    const f = editing.value[convId] ? stashed.get(convId) : forms.value[convId]
    if (f && untouched(f, prevCid)) {
      f.customerId = nextCid
      if (!editing.value[convId]) {
        quotes.value[convId] = { ...IDLE_QUOTE }
        scheduleQuote(convId)
      }
    }
    if (nextCid && histories.value[nextCid]?.state !== 'loading') void loadHistory(nextCid)
    tellLink(convId)
  }

  /** The open chat's cached history again (its orders changed: saved / paid / held / linked on any screen). */
  function reloadHistory(convId: string): void {
    const ids = new Set([forms.value[convId]?.customerId ?? null, contexts.value[convId]?.customer_id ?? null])
    for (const cid of ids) {
      if (cid && histories.value[cid] && histories.value[cid].state !== 'loading') void loadHistory(cid)
    }
  }

  async function refreshContext(convId: string): Promise<void> {
    listen()
    const epoch = linkEpoch.get(convId) ?? 0
    try {
      const ctx = await api().context(convId)
      const prev = contexts.value[convId]
      // linked / unlinked on this screen while this answer was on its way: the link as this screen set it stays
      if ((linkEpoch.get(convId) ?? 0) !== epoch && prev) ctx.customer_id = prev.customer_id
      contexts.value[convId] = ctx
      contextErrors.value[convId] = null
      setOpen(convId, ctx.open_order ?? null)
      // the held order on the saved screen was paid / removed (here, on another screen or on the POS sale page) — or
      // is still held but changed (the customer is paying the link, a colleague renewed / edited it): the screen takes it
      const r = results.value[convId]
      if (r?.chat_order.status === 'held') {
        const now = ctx.chat_orders.find((o) => o.id === r.chat_order.id)
        const still = (ctx.held_orders ?? []).find((o) => o.id === r.chat_order.id && o.status === 'held')
        const merged = still ? { ...r.chat_order, ...still } : null
        if (now && now.status !== 'held') void settleHeld(convId, now.id)
        else if (merged && JSON.stringify(merged) !== JSON.stringify(r.chat_order)) results.value[convId] = { ...r, chat_order: merged }
      }
      // the held order being edited was paid / removed meanwhile: nothing left to edit (only on the server's word — an
      // answer that left before it was saved does not know it yet)
      const ed = editing.value[convId]
      const was = ed ? ctx.chat_orders.find((o) => o.id === ed.id) : null
      if (ed && was && was.status !== 'held') {
        endEdit(convId)
        useInbox().flash(
          ed.card
            ? 'ลิงก์ที่กำลังแก้ ลูกค้าชำระแล้วหรือถูกยกเลิกแล้ว การแก้ไขไม่ได้บันทึก'
            : 'พักบิลที่กำลังแก้ ยืนยันชำระแล้วหรือถูกลบที่ POS แล้ว การแก้ไขไม่ได้บันทึก',
        )
      }
      const before = prev ? (prev.customer_id ?? null) : undefined
      if (before !== undefined && before !== (ctx.customer_id ?? null)) linkedElsewhere(convId, before, ctx.customer_id ?? null)
    } catch (e) {
      contextErrors.value[convId] = errText(e)
    }
  }

  const contextLoads = new Map<string, Promise<void>>()
  /** The context once (the slip buttons in the thread need the chat's held orders even when the tab is closed); from then
   *  on realtime keeps it fresh (a reload no longer leaves "ยืนยันชำระ" / the fill check stale until the order tab opens). */
  function ensureContext(convId: string): Promise<void> {
    listen()
    if (contexts.value[convId]) return Promise.resolve()
    let p = contextLoads.get(convId)
    if (!p) {
      p = refreshContext(convId).finally(() => contextLoads.delete(convId))
      contextLoads.set(convId, p)
    }
    return p
  }

  // ------------------------------------------------ "ยังไม่โอน · ส่ง QR ให้ลูกค้า" (held bills)
  /** The chat's orders waiting for the transfer (newest first). */
  function heldOrders(convId: string): ChatOrderRef[] {
    return (contexts.value[convId]?.held_orders ?? []).filter((o) => o.status === 'held')
  }

  /** One chat order as the server keeps it (the held order's cart for its summary text). */
  function loadChatOrder(id: string): Promise<PosChatOrderState> {
    return api().chatOrder(id)
  }

  async function settleHeld(convId: string, id: string): Promise<void> {
    try {
      const st = await api().chatOrder(id)
      const r = results.value[convId]
      if (r && r.chat_order.id === id) results.value[convId] = { chat_order: st.chat_order, order: st.order, replayed: st.replayed }
    } catch {
      /* the next context refresh tells */
    }
  }

  /** A text for the customer into the chat's typing box (staff check it and press send — never sent by itself): an untouched
   *  AI draft is replaced, anything the staff typed stays above it. */
  function fillComposer(convId: string, text: string): void {
    const inbox = useInbox()
    const st = inbox.composers[convId]
    const keep = st && st.text.trim() && !(st.origin === 'ai' && !st.edited) ? st.text.trimEnd() : ''
    inbox.setText(convId, '')
    inbox.setText(convId, keep ? `${keep}\n\n${text}` : text)
  }

  const qrBusy = ref<Record<string, boolean>>({})
  /** "ส่งใบสรุปยอดและ QR ในแชท": the PromptPay QR of a held order, staged in the composer (staff press send). */
  async function stageQr(convId: string, chatOrderId: string): Promise<PosQr> {
    qrBusy.value[chatOrderId] = true
    try {
      const res = await api().promptpayQr(chatOrderId)
      useLibrary().stage(convId, res.asset, 'manual')
      // the order as the server keeps it now (its QR; a new QR ends "ส่งใบสรุปยอดและ QR ใหม่")
      if (res.chat_order?.chat_order) applyOrderRef(convId, res.chat_order.chat_order)
      return res
    } finally {
      qrBusy.value[chatOrderId] = false
    }
  }

  const payBusy = ref<Record<string, boolean>>({})
  /** "ยืนยันชำระ" of a held order: the POS makes the order (the saved screen then shows it, with its bill). */
  async function confirmPay(convId: string, chatOrderId: string, body: PosPayBody): Promise<PayOutcome> {
    if (payBusy.value[chatOrderId]) return { kind: 'error', message: 'กำลังยืนยันชำระอยู่', settled: false }
    payBusy.value[chatOrderId] = true
    try {
      const st = await api().payChatOrder(chatOrderId, body)
      results.value[convId] = { chat_order: st.chat_order, order: st.order, replayed: !!st.replayed }
      void refreshContext(convId)
      if (st.chat_order.customer_id) void loadHistory(st.chat_order.customer_id)
      return { kind: 'done', order: st.chat_order }
    } catch (e) {
      if (e instanceof PosApiError && e.reason === 'amountMismatch' && e.detail && typeof e.detail === 'object') {
        return { kind: 'mismatch', diff: e.detail as PosAmountDiff, message: e.message }
      }
      // paid already (another screen / the POS sale page) or removed in the POS: the tab shows how it ended
      const settled = e instanceof PosApiError && ['orderPaid', 'holdRemoved', 'notHeld'].includes(e.reason)
      // holdChanged: the held bill changed while the POS priced it (an edit landed) — nothing issued, the card reloads
      if (settled || (e instanceof PosApiError && e.reason === 'holdChanged')) void refreshContext(convId)
      return { kind: 'error', message: errText(e), settled }
    } finally {
      payBusy.value[chatOrderId] = false
    }
  }

  // ------------------------------------------------ "บัตรเครดิต · ส่งลิงก์ให้ลูกค้า" · "ยกเลิกลิงก์" / "ยกเลิกพักบิล"
  /** A cancel / renew / refresh of the order is on its way (its buttons wait). */
  const cardBusy = ref<Record<string, 'cancel' | 'renew' | 'refresh' | null>>({})

  /** The order's new state from the server: its card / the saved screen take it, the context reloads. */
  function tookState(convId: string, st: PosChatOrderState): void {
    applyOrderRef(convId, st.chat_order, st)
    void refreshContext(convId)
  }

  /** What a refused card action means for the tab: paid / cancelled / removed meanwhile = the context shows how it ended. */
  function settledByError(convId: string, e: unknown): void {
    if (e instanceof PosApiError && ['orderPaid', 'holdRemoved', 'notHeld', 'cardPaying', 'holdEdited'].includes(e.reason)) {
      void refreshContext(convId)
    }
  }

  /** "ยกเลิกลิงก์" (a card link can never be paid after it) / "ยกเลิกพักบิล" (a held QR bill): the order becomes void.
   *  Throws the server's calm Thai reason (paying right now, paid meanwhile, …). */
  async function cancelOrder(convId: string, chatOrderId: string): Promise<ChatOrderRef> {
    if (cardBusy.value[chatOrderId]) throw new Error('กำลังทำรายการอยู่')
    cardBusy.value[chatOrderId] = 'cancel'
    try {
      const st = await api().cancelChatOrder(chatOrderId)
      tookState(convId, st)
      return st.chat_order
    } catch (e) {
      settledByError(convId, e)
      throw e
    } finally {
      cardBusy.value[chatOrderId] = null
    }
  }

  /** "ต่ออายุ" of a card link: a new delivery day; it then expires 21:30 the day before it. Throws the server's reason. */
  async function renewCard(convId: string, chatOrderId: string, body: PosRenewBody): Promise<ChatOrderRef> {
    if (cardBusy.value[chatOrderId]) throw new Error('กำลังทำรายการอยู่')
    cardBusy.value[chatOrderId] = 'renew'
    try {
      const st = await api().renewChatOrder(chatOrderId, body)
      tookState(convId, st)
      return st.chat_order
    } catch (e) {
      if (e instanceof PosApiError && e.reason === 'dayLocked') void loadCatalog(true)
      settledByError(convId, e)
      throw e
    } finally {
      cardBusy.value[chatOrderId] = null
    }
  }

  /** "ตรวจสถานะตอนนี้" of a card link (the server asks the POS: paid?). */
  async function refreshCard(convId: string, chatOrderId: string): Promise<ChatOrderRef> {
    if (cardBusy.value[chatOrderId]) throw new Error('กำลังทำรายการอยู่')
    cardBusy.value[chatOrderId] = 'refresh'
    try {
      const st = await api().refreshChatOrder(chatOrderId)
      tookState(convId, st)
      return st.chat_order
    } finally {
      cardBusy.value[chatOrderId] = null
    }
  }

  // ------------------------------------------------ the held order first · "แก้ไขออเดอร์"
  /** "ลงออเดอร์ใหม่อีกใบ (ลูกค้าสั่งเพิ่ม)" pressed: the new-order form shows even though the chat has a held order. */
  const newOrderOpen = ref<Record<string, boolean>>({})
  /** The held order whose edit form is open (the form is prefilled from its `hold.cart`). */
  const editing = ref<Record<string, ChatOrderRef | null>>({})
  /** The edit click that got no answer: same edit → same client_id (the POS replays it). */
  const editClicks = ref<Record<string, { clientId: string; fp: string } | null>>({})
  /** The new-order form put aside while an edit is open (back after it). */
  const stashed = new Map<string, OrderForm | undefined>()

  /** The chat has a held order and the staff did not ask for another order: the tab shows the held order(s) instead of
   *  the new-order form, and nothing fills that form (owner 18 ต.ค. 69 — also after a reload: the server context). */
  function heldGate(convId: string): boolean {
    if (newOrderOpen.value[convId] || editing.value[convId]) return false
    const shown = results.value[convId]?.chat_order.id
    return heldOrders(convId).some((o) => o.id !== shown)
  }

  /** The automatic fills (the pack the chat chose, the AI's cart — stores/script.ts) stay away: the chat's context is
   *  unknown yet, it has a held order (its chat describes that order), or an edit is open. */
  function autoFillBlocked(convId: string): boolean {
    return !contexts.value[convId] || heldOrders(convId).length > 0 || !!editing.value[convId]
  }

  /** Why a cart from outside the tab (สั่งชุดเดิมซ้ำ · เปิดลงออเดอร์ (เติมให้แล้ว) · สั่งซ้ำ · สั่งต่อ · อ่านเมนู) cannot go into
   *  the form now (null = it can). `intoEdit`: dishes read from the chat may go into the held order being edited. */
  function fillBlock(convId: string, intoEdit = false): string | null {
    if (editing.value[convId]) return intoEdit ? null : 'กำลังแก้ไขออเดอร์รอชำระอยู่ บันทึกหรือยกเลิกการแก้ไขก่อน'
    if (heldGate(convId)) {
      const card = heldOrders(convId).some((o) => !!o.card)
      return `แชทนี้มีออเดอร์รอชำระ (${card ? 'ส่งลิงก์ชำระบัตรแล้ว' : 'พักบิลไว้ใน POS'}) · ลูกค้าขอเปลี่ยน กด "แก้ไขออเดอร์" ที่การ์ด · ลูกค้าสั่งเพิ่ม กด "ลงออเดอร์ใหม่อีกใบ" ก่อน`
    }
    return null
  }

  /** "ลงออเดอร์ใหม่อีกใบ (ลูกค้าสั่งเพิ่ม)": a new, empty form for the same customer. */
  function openNewOrder(convId: string): void {
    startNew(convId)
    newOrderOpen.value[convId] = true
  }

  /** "แก้ไขออเดอร์": the form prefilled from the held order. false = nothing to prefill from (an older API). */
  function startEdit(convId: string, order: ChatOrderRef): boolean {
    const f = heldForm(order, clock.now, catalog.value)
    if (!f) return false
    if (!editing.value[convId]) stashed.set(convId, forms.value[convId])
    editing.value[convId] = order
    editClicks.value[convId] = null
    forms.value[convId] = f
    quotes.value[convId] = { ...IDLE_QUOTE }
    saveErrors.value[convId] = null
    if (f.customerId && histories.value[f.customerId]?.state !== 'ok') void loadHistory(f.customerId)
    scheduleQuote(convId)
    return true
  }

  /** Back to where the edit started from (the held order's card): the form put aside comes back. */
  function endEdit(convId: string): void {
    if (!editing.value[convId]) return
    editing.value[convId] = null
    editClicks.value[convId] = null
    const prev = stashed.get(convId)
    stashed.delete(convId)
    forms.value[convId] = prev ?? emptyForm(clock.now, contexts.value[convId]?.customer_id ?? null, catalog.value)
    quotes.value[convId] = { ...IDLE_QUOTE }
    saveErrors.value[convId] = null
    scheduleQuote(convId)
  }

  /** One of the chat's orders as the server keeps it now (after an edit / a new QR): its card and the saved screen showing
   *  it take it. `st`: the POS answer that goes with it (an edit's new cart); without it the saved screen keeps its own. */
  function applyOrderRef(convId: string, co: ChatOrderRef, st?: PosChatOrderState): void {
    const ctx = contexts.value[convId]
    if (ctx) {
      ctx.held_orders = (ctx.held_orders ?? []).map((o) => (o.id === co.id ? co : o))
      ctx.chat_orders = ctx.chat_orders.map((o) => (o.id === co.id ? co : o))
    }
    const r = results.value[convId]
    if (r && r.chat_order.id === co.id) {
      results.value[convId] = st ? { chat_order: co, order: st.order, replayed: !!st.replayed } : { ...r, chat_order: co }
    }
  }

  /** The newest the tab knows of a chat order (the context list, the saved screen, else `fallback`). */
  function latest(convId: string, fallback: ChatOrderRef): ChatOrderRef {
    const r = results.value[convId]?.chat_order
    return heldOrders(convId).find((o) => o.id === fallback.id) ?? (r?.id === fallback.id ? r : fallback)
  }

  /** "บันทึกการแก้ไข": the same held bill in the POS (hold code unchanged). The old QR is never sent again by mistake: a new
   *  one is made for the new amount (the staged old picture leaves the composer). */
  async function saveEdit(convId: string): Promise<boolean> {
    const o = editing.value[convId]
    if (!o || saving.value[convId]) return false
    const list = issues(convId)
    if (list.length) {
      saveErrors.value[convId] = list[0] ?? null
      return false
    }
    const base = orderBody(convId, form(convId), catalog.value)
    if (!base) return false
    const cart = o.hold?.cart ?? o.card?.cart
    const body: Omit<PosHoldEditBody, 'client_id'> = {
      ...base,
      // a card link stays a card link (the server cancels the old link and makes a new one)
      payment: { method: o.card ? 'CardLink' : 'PendingTransfer' },
      // what the form has no field for stays as the held order had it
      ...(cart?.spoon ? { spoon: true } : {}),
      ...(cart?.location_id && cart.shipping === base.shipping ? { location_id: cart.location_id } : {}),
      edit_count: o.hold?.edit_count ?? o.card?.edit_count ?? 0,
    }
    const fp = fingerprint(body)
    let click = editClicks.value[convId]
    if (!click || click.fp !== fp) click = editClicks.value[convId] = { clientId: newClientId(), fp }
    saving.value[convId] = true
    saveErrors.value[convId] = null
    try {
      const st = await api().editChatOrder(o.id, { ...body, client_id: click.clientId })
      const oldQr = latest(convId, o).hold?.qr_media_id ?? o.hold?.qr_media_id
      if (oldQr) useLibrary().unstageAsset(convId, oldQr)
      applyOrderRef(convId, st.chat_order, st)
      endEdit(convId)
      void refreshContext(convId)
      return true
    } catch (e) {
      if (!(e instanceof PosApiError && e.retrySame)) editClicks.value[convId] = null
      if (e instanceof PosApiError && e.reason === 'dayLocked') void loadCatalog(true)
      saveErrors.value[convId] = errText(e)
      // confirmed / removed meanwhile: nothing to edit any more — the tab shows how it ended
      if (e instanceof PosApiError && ['orderPaid', 'holdRemoved', 'notHeld'].includes(e.reason)) {
        endEdit(convId)
        useInbox().flash(errText(e))
        void refreshContext(convId)
      } else if (e instanceof PosApiError && e.reason === 'holdEdited') {
        void refreshContext(convId)
      }
      return false
    } finally {
      saving.value[convId] = false
    }
  }

  // ------------------------------------------------ a save the POS has not answered yet
  /** The waiting save changed. The one shown before is gone → ask how it ended (done / refused). */
  function setOpen(convId: string, next: PosOpenOrder | null): void {
    const prev = openOrders.value[convId] ?? null
    openOrders.value[convId] = next
    if (prev && prev.id !== next?.id) void settle(convId, prev.id)
  }

  async function settle(convId: string, id: string): Promise<void> {
    try {
      applyState(convId, await api().chatOrder(id))
    } catch {
      /* the next context refresh tells */
    }
  }

  /** A chat order's state from the server: still waiting → shown · done → the saved screen · refused → why. */
  function applyState(convId: string, st: PosChatOrderState): void {
    if (st.open_order) {
      openOrders.value[convId] = st.open_order
      return
    }
    if (openOrders.value[convId]?.id === st.chat_order.id) openOrders.value[convId] = null
    // held = the POS answered with a held bill ("ยังไม่โอน"): the saved screen of a held order
    if (st.chat_order.status === 'done' || st.chat_order.status === 'held') {
      results.value[convId] = { chat_order: st.chat_order, order: st.order, replayed: st.replayed, followed_up: st.followed_up }
      pendingClicks.value[convId] = null
      saveErrors.value[convId] = null
      if (st.chat_order.customer_id) void loadHistory(st.chat_order.customer_id)
    } else if (st.chat_order.status === 'rejected') {
      pendingClicks.value[convId] = null
      saveErrors.value[convId] = `ออเดอร์ที่รอผลจาก POS ไม่ได้ลง: ${st.chat_order.error_message || 'POS ไม่รับ'} · แก้แล้วบันทึกใหม่ได้`
    } else if (st.chat_order.status === 'abandoned') {
      // cancelled by the owner/head: that click is never sent again — the next save is a new order
      pendingClicks.value[convId] = null
      saveErrors.value[convId] = null
    }
  }

  /** "ยกเลิกคำขอนี้" (owner/head, after the POS was checked). Throws the server's Thai reason (the dialog shows it). */
  async function abandon(convId: string): Promise<void> {
    const oo = openOrders.value[convId]
    if (!oo) return
    applyState(convId, await api().abandonChatOrder(oo.id))
  }

  /** "ตรวจผลตอนนี้": the server asks the POS about the waiting save right now (same key, never written twice). */
  async function checkNow(convId: string): Promise<void> {
    const oo = openOrders.value[convId]
    if (!oo || checking.value[convId]) return
    checking.value[convId] = true
    try {
      applyState(convId, await api().checkChatOrder(oo.id))
    } catch (e) {
      saveErrors.value[convId] = errText(e)
    } finally {
      checking.value[convId] = false
    }
  }

  /** Realtime `pos_order` (api/app/pos_api.py notify_order: a chat order saved / held / paid / edited / followed up, the
   *  chat linked / unlinked to a POS customer — on any screen or found on the POS): that chat's context reloads, and for
   *  the open chat its cached history too. A resync (reconnect after a deploy, the tab visible again — events may have
   *  been missed) reloads the open chat and every chat that waits on something: a save the POS has not answered, a held
   *  QR bill or a card link, a context that failed to load. */
  function onEvent(e: RealtimeEvent | null): void {
    const open = useInbox().selectedId
    const reload = (id: string): void => {
      void refreshContext(id).then(() => {
        if (id === useInbox().selectedId) reloadHistory(id)
      })
    }
    if (!e) {
      const ids = new Set<string>()
      if (open && (contexts.value[open] || contextErrors.value[open])) ids.add(open)
      for (const [id, oo] of Object.entries(openOrders.value)) if (oo) ids.add(id)
      for (const id of Object.keys(contexts.value)) if (heldOrders(id).length) ids.add(id)
      for (const [id, r] of Object.entries(results.value)) if (r?.chat_order.status === 'held') ids.add(id)
      for (const [id, err] of Object.entries(contextErrors.value)) if (err) ids.add(id)
      for (const id of ids) reload(id)
      return
    }
    if (e.type !== 'conversation.updated' || !e.pos_order) return
    const id = e.conversation.id
    if (contexts.value[id] || openOrders.value[id]) reload(id)
  }
  let off: (() => void) | null = null
  /** From the first context on (any of: the order / history tab, the customer card, a slip button): idempotent. */
  function listen(): void {
    if (!off) off = useInbox().onRealtime(onEvent)
  }

  /** Open the tab for a conversation: catalog, context (linked customer, POS channel/staff), history. */
  async function open(convId: string): Promise<void> {
    listen()
    // a catalog of an earlier Bangkok day (the page stayed open overnight): today, the default day, locked days moved on
    const day = catalog.value?.delivery?.today
    await Promise.all([loadCatalog(!!day && day !== dayKey(clock.now)), refreshContext(convId)])
    const ctx = contexts.value[convId]
    const f = form(convId)
    if (ctx?.customer_id && !f.customerId) f.customerId = ctx.customer_id
    // the form may exist from before the catalog arrived: follow the POS default day until the staff picks one
    if (f.dateAuto && catalog.value) {
      const dl = deliveryOf(catalog.value, clock.now)
      f.date = firstOpenDay(dl, dl.default_date, f.shipping) // never a holiday / day off
    }
    const cid = f.customerId ?? ctx?.customer_id ?? null
    if (cid && histories.value[cid]?.state !== 'loading') await loadHistory(cid)
    scheduleQuote(convId)
  }

  // ------------------------------------------------ customer
  /** The new-order form — put aside while an edit is open (the held order's customer never changes): what linking or
   *  adding a customer changes. */
  function orderForm(convId: string): OrderForm {
    if (!editing.value[convId]) return form(convId)
    let f = stashed.get(convId)
    if (!f) {
      f = emptyForm(clock.now, contexts.value[convId]?.customer_id ?? null, catalog.value)
      stashed.set(convId, f)
    }
    return f
  }

  async function linkCustomer(convId: string, c: PosCustomer): Promise<void> {
    const f = orderForm(convId)
    remember(c)
    const ctx = contexts.value[convId]
    if (ctx?.can_link !== false) {
      remember(await api().linkCustomer(convId, c.id))
      const changed = ctx?.customer_id !== c.id
      if (ctx) ctx.customer_id = c.id
      if (changed) linkedHere(convId)
    }
    if (f.customerId !== c.id) {
      f.customerId = c.id
      f.continueHeaderId = null
      if (f.coupon.state !== 'none') clearCoupon(convId)
      f.source = null
      f.shipping = c.has_pin === false ? 'dhl' : 'lalamove'
    }
    void loadHistory(c.id)
    scheduleQuote(convId)
  }

  async function unlinkCustomer(convId: string): Promise<void> {
    const ctx = contexts.value[convId]
    if (ctx?.can_link !== false) await api().unlinkCustomer(convId)
    const changed = !!ctx?.customer_id
    if (ctx) ctx.customer_id = null
    if (changed) linkedHere(convId)
    const f = orderForm(convId)
    f.customerId = null
    f.continueHeaderId = null
    f.source = null
    if (!editing.value[convId]) quotes.value[convId] = { ...IDLE_QUOTE }
  }

  function searchCustomers(q: string, opts: { exactMobile?: boolean } = {}): Promise<PosCustomer[]> {
    return api().searchCustomers(q, opts)
  }

  /** "สร้างลูกค้าใหม่" from the phone button under a message: the order tab opens its create form with this phone ·
   *  "เปิดลงออเดอร์ (เติมให้แล้ว)" of the sales steps: with the place the chat gave (stores/script.ts fillFromChat). */
  const createRequests = ref<Record<string, CreateRequest | null>>({})
  function requestCreate(
    convId: string,
    mobile: string,
    extra: { address?: PrefillAddress | null; shipping?: PosShipping | null; name?: string | null; keep?: boolean } = {},
  ): void {
    createRequests.value[convId] = {
      mobile,
      at: Date.now(),
      address: extra.address ?? null,
      shipping: extra.shipping ?? null,
      ...(extra.name ? { name: extra.name } : {}),
      ...(extra.keep ? { keep: true } : {}),
    }
  }

  async function createCustomer(convId: string, body: Omit<PosCustomerCreate, 'conversation_id'>): Promise<PosCustomerCreated> {
    const res = await api().createCustomer({ ...body, conversation_id: convId })
    remember(res.customer)
    const f = orderForm(convId)
    f.customerId = res.customer.id
    f.shipping = body.shipping
    if (res.linked) {
      if (contexts.value[convId]) contexts.value[convId].customer_id = res.customer.id
      linkedHere(convId)
    }
    void loadHistory(res.customer.id)
    scheduleQuote(convId)
    return res
  }

  // ------------------------------------------------ หมุด Lalamove (owner 18 ต.ค. 69 "ถ้า lalamove ยังไงต้องมีหมุด")
  /** The place the customer gave in each chat (a shared location / a Google Maps link they pasted) — the pin field's
   *  prefill in CustomerBlock, asked when the field shows. */
  const sharedPlaces = ref<Record<string, Loadable<PosSharedPlace | null>>>({})
  async function loadSharedPin(convId: string): Promise<void> {
    if (sharedPlaces.value[convId]?.state === 'loading') return
    sharedPlaces.value[convId] = { state: 'loading', data: sharedPlaces.value[convId]?.data ?? null, error: null }
    try {
      sharedPlaces.value[convId] = { state: 'ok', data: await api().sharedPin(convId), error: null }
    } catch (e) {
      sharedPlaces.value[convId] = { state: 'error', data: null, error: errText(e) }
    }
  }

  /** "บันทึกหมุด" of a POS customer without a pin (only the pin — the POS address text stays): the chip turns
   *  "มีหมุด Lalamove" and the fee is priced again (the same cart, so the cached quote is dropped). Throws the server's
   *  Thai reason; pinExists (someone set a pin meanwhile) reloads the customer so the tab shows it. */
  async function setPin(convId: string, customerId: string, pin: { lat: number; lng: number }): Promise<PosPinResult> {
    try {
      const res = await api().setCustomerPin(customerId, { conversation_id: convId, lat: pin.lat, lng: pin.lng })
      remember(res.customer)
      const q = quotes.value[convId]
      if (q) quotes.value[convId] = { ...q, key: null }
      scheduleQuote(convId)
      return res
    } catch (e) {
      if (e instanceof PosApiError && e.reason === 'pinExists') void loadHistory(customerId)
      throw e
    }
  }

  // ------------------------------------------------ cart edits
  /** Apply a change and its side effects (coupon staleness, rounds, live quote). */
  function update(convId: string, patch: Partial<OrderForm>): void {
    const f = form(convId)
    const packChanged =
      ('setMenuId' in patch && patch.setMenuId !== f.setMenuId) ||
      ('continueHeaderId' in patch && patch.continueHeaderId !== f.continueHeaderId)
    Object.assign(f, patch)
    if ('date' in patch) f.dateAuto = false
    if (packChanged) {
      const c = f.coupon
      if (f.continueHeaderId && c.state !== 'none') {
        f.coupon = { state: 'error', code: 'code' in c ? c.code : f.couponCode, message: 'สั่งต่อใช้คูปองส่วนลดไม่ได้ (กติกาหน้าขาย)' }
      } else if (c.state === 'ok' && c.setMenuId !== f.setMenuId) {
        f.coupon = { state: 'stale', code: c.code, discount: c.discount }
      }
    }
    if ('shipping' in patch && patch.shipping === 'dhl') f.time = null
    if ('couponCode' in patch && f.coupon.state !== 'none' && 'code' in f.coupon && f.coupon.code !== f.couponCode.trim()) {
      f.coupon = { state: 'none' }
    }
    saveErrors.value[convId] = null
    scheduleQuote(convId)
  }

  function setQty(convId: string, menuId: number, qty: number): void {
    update(convId, { items: withQty(form(convId).items, menuId, qty) })
  }

  /**
   * "ยืนยันลงออเดอร์" of อ่านเมนูจากแชท (stores/menuRead.ts): the confirmed list replaces the menus picked so far (owner
   * 28 ก.ย. 69 — no question asked, the modal says what it replaces) and sets the pack chosen in the modal. A chat that
   * continues an open pack keeps continuing it.
   */
  function applyMenuRead(convId: string, p: { setMenuId?: number | null; items: PosCartItem[] }): void {
    const f = form(convId)
    const patch: Partial<OrderForm> = { items: p.items.map((i) => ({ menu_id: i.menu_id, qty: i.qty })) }
    if (p.setMenuId !== undefined && p.setMenuId !== null && !f.continueHeaderId && p.setMenuId !== f.setMenuId) patch.setMenuId = p.setMenuId
    update(convId, patch)
  }

  function startContinue(convId: string, pack: PosOpenPack): void {
    update(convId, {
      continueHeaderId: pack.order_header_id,
      setMenuId: null,
      source: { kind: 'continue', label: `สั่งต่อ ${pack.set_menu_name} · เหลือ ${pack.meals_remaining} มื้อ` },
    })
  }

  function stopContinue(convId: string): void {
    const f = form(convId)
    update(convId, { continueHeaderId: null, source: null, coupon: f.coupon.state === 'error' ? { state: 'none' } : f.coupon })
  }

  /**
   * "สั่งซ้ำ" from history: same pack as a new order (menus are chosen again). Waits for the POS catalog when it is not
   * loaded yet (a quick click on a fresh page used to leave the pack at "เลือกแพ็ก…"). false = the pack is off sale.
   */
  async function repeat(convId: string, order: PosHistoryOrder): Promise<boolean> {
    if (!catalog.value) {
      try {
        await loadCatalog()
      } catch {
        /* the order tab shows the catalog error */
      }
    }
    if (!catalog.value) return true // unknown yet: no "closed" message
    const id = packForName(catalog.value, order.set_menu_name)
    update(convId, {
      continueHeaderId: null,
      setMenuId: id,
      shipping: order.carrier === 'dhl' ? 'dhl' : 'lalamove',
      source: { kind: 'repeat', label: `สั่งซ้ำจากออเดอร์ ${order.order_code}` },
    })
    return id !== null
  }

  // ------------------------------------------------ coupon
  async function applyCoupon(convId: string): Promise<void> {
    const f = form(convId)
    const code = f.couponCode.trim().toUpperCase()
    if (!code || !f.customerId) return
    if (f.continueHeaderId) {
      f.coupon = { state: 'error', code, message: 'สั่งต่อใช้คูปองส่วนลดไม่ได้ (กติกาหน้าขาย)' }
      return
    }
    if (!f.setMenuId) {
      f.coupon = { state: 'error', code, message: 'เลือกแพ็กก่อนใส่โค้ด' }
      return
    }
    f.couponCode = code
    f.coupon = { state: 'checking', code }
    try {
      const r = await api().checkCoupon({
        conversation_id: convId,
        customer_id: Number(f.customerId),
        set_menu_id: f.setMenuId,
        items: f.items.map((i) => ({ ...i })),
        coupon_code: code,
      })
      if (f.couponCode.trim().toUpperCase() !== code) return // typed something else meanwhile
      const label = r.unit === 'percen' ? `ลด ${r.benefits}%` : r.unit === 'bath' ? `ลด ${r.benefits} บาท` : `ฟรี ${r.free_items} กล่อง`
      f.coupon = {
        state: 'ok',
        code: r.code,
        token: r.token,
        discount: r.discount,
        freeItems: r.free_items,
        setMenuId: r.set_menu_id ?? f.setMenuId,
        label,
      }
      // pack changed while checking: the token belongs to the old pack
      if (f.coupon.state === 'ok' && f.coupon.setMenuId !== f.setMenuId) {
        f.coupon = { state: 'stale', code: r.code, discount: r.discount }
      }
    } catch (e) {
      if (f.couponCode.trim().toUpperCase() !== code) return // typed something else meanwhile
      f.coupon = { state: 'error', code, message: errText(e) }
    }
    scheduleQuote(convId)
  }

  function clearCoupon(convId: string): void {
    const f = form(convId)
    f.coupon = { state: 'none' }
    f.couponCode = ''
    scheduleQuote(convId)
  }

  // ------------------------------------------------ live quote
  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  const quoteSeq = new Map<string, number>()

  function currentKey(convId: string): string | null {
    const body = cartBody(convId, form(convId), catalog.value)
    return body ? fingerprint(body) : null
  }

  function quoteOf(convId: string): QuoteState & { fresh: boolean } {
    const q = quotes.value[convId] ?? IDLE_QUOTE
    return { ...q, fresh: q.key !== null && q.key === currentKey(convId) }
  }

  function scheduleQuote(convId: string): void {
    const t = timers.get(convId)
    if (t) clearTimeout(t)
    const key = currentKey(convId)
    const cur = quotes.value[convId]
    if (key === null) {
      quotes.value[convId] = { ...IDLE_QUOTE }
      return
    }
    if (cur && cur.key === key && cur.state === 'ok') return
    // keep showing the last numbers while the new ones are computed
    quotes.value[convId] = { state: 'loading', data: cur?.data ?? null, error: null, key: cur?.key ?? null }
    if (quoteDelayMs.value <= 0) {
      void runQuote(convId)
      return
    }
    timers.set(
      convId,
      setTimeout(() => {
        timers.delete(convId)
        void runQuote(convId)
      }, quoteDelayMs.value),
    )
  }

  async function runQuote(convId: string): Promise<void> {
    const body = cartBody(convId, form(convId), catalog.value)
    if (!body) return
    const key = fingerprint(body)
    const seq = (quoteSeq.get(convId) ?? 0) + 1
    quoteSeq.set(convId, seq)
    try {
      const data = await api().quote(body)
      if (quoteSeq.get(convId) !== seq) return
      quotes.value[convId] = { state: 'ok', data, error: null, key }
    } catch (e) {
      if (quoteSeq.get(convId) !== seq) return
      const f = form(convId)
      if (e instanceof PosApiError && e.reason === 'couponStale' && f.coupon.state === 'ok') {
        f.coupon = { state: 'stale', code: f.coupon.code, discount: f.coupon.discount }
        scheduleQuote(convId)
        return
      }
      quotes.value[convId] = { state: 'error', data: quotes.value[convId]?.data ?? null, error: errText(e), key }
    }
  }

  // ------------------------------------------------ save
  function blockers(convId: string): string[] {
    const ctx = contexts.value[convId]
    const out: string[] = []
    if (contextErrors.value[convId]) out.push(contextErrors.value[convId] as string)
    if (ctx?.staff_error) out.push(ctx.staff_error.message)
    if (ctx?.channel_error) out.push(ctx.channel_error.message)
    if (!catalog.value) out.push(catalogError.value ?? 'กำลังโหลดแพ็กและเมนู')
    return out
  }

  function issues(convId: string): string[] {
    const f = form(convId)
    const hasPin = f.customerId ? (customers.value[f.customerId]?.has_pin ?? null) : null
    return saveIssues(f, quoteOf(convId), blockers(convId), catalog.value ? deliveryOf(catalog.value, clock.now) : null, hasPin,
      clock.now)
  }

  async function save(convId: string): Promise<boolean> {
    if (saving.value[convId]) return false
    const list = issues(convId)
    if (list.length) {
      saveErrors.value[convId] = list[0] ?? null
      return false
    }
    const base = orderBody(convId, form(convId), catalog.value)
    if (!base) return false
    const fp = fingerprint(base)
    let click = pendingClicks.value[convId]
    if (!click || click.fp !== fp) click = pendingClicks.value[convId] = { clientId: newClientId(), fp }
    saving.value[convId] = true
    saveErrors.value[convId] = null
    try {
      const res = await api().createOrder({ ...base, client_id: click.clientId })
      results.value[convId] = res
      pendingClicks.value[convId] = null
      const cid = form(convId).customerId
      void refreshContext(convId)
      if (cid) void loadHistory(cid)
      return true
    } catch (e) {
      const retry = e instanceof PosApiError && e.retrySame
      if (!retry) pendingClicks.value[convId] = null
      // someone dispatched that day meanwhile: reload locked dates so the picker shows it
      if (e instanceof PosApiError && e.reason === 'dayLocked') void loadCatalog(true)
      saveErrors.value[convId] = errText(e)
      // no answer (the server follows it up) / another save of this chat still waiting: show that one
      if (e instanceof PosApiError && e.openOrder) openOrders.value[convId] = e.openOrder
      return false
    } finally {
      saving.value[convId] = false
    }
  }

  /** After a saved order: a fresh form for the same customer. */
  function startNew(convId: string): void {
    const cid = form(convId).customerId
    forms.value[convId] = emptyForm(clock.now, cid, catalog.value)
    results.value[convId] = null
    saveErrors.value[convId] = null
    quotes.value[convId] = { ...IDLE_QUOTE }
  }

  return {
    catalog,
    catalogError,
    contexts,
    contextErrors,
    forms,
    quotes,
    customers,
    histories,
    saving,
    saveErrors,
    results,
    pendingClicks,
    openOrders,
    checking,
    quoteDelayMs,
    loadCatalog,
    form,
    open,
    refreshContext,
    loadHistory,
    linkCustomer,
    unlinkCustomer,
    searchCustomers,
    createRequests,
    requestCreate,
    createCustomer,
    sharedPlaces,
    loadSharedPin,
    setPin,
    update,
    setQty,
    applyMenuRead,
    startContinue,
    stopContinue,
    repeat,
    applyCoupon,
    clearCoupon,
    quoteOf,
    scheduleQuote,
    runQuote,
    issues,
    save,
    startNew,
    checkNow,
    abandon,
    onEvent,
    onLinkChange,
    ensureContext,
    heldOrders,
    loadChatOrder,
    fillComposer,
    qrBusy,
    stageQr,
    payBusy,
    confirmPay,
    cardBusy,
    cancelOrder,
    renewCard,
    refreshCard,
    newOrderOpen,
    editing,
    heldGate,
    autoFillBlocked,
    fillBlock,
    openNewOrder,
    startEdit,
    endEdit,
    saveEdit,
  }
})
